/**
 * space_combat_hud.ts — the sector HUD's AIMING + EW layer (space campaign I, 2026-09-30). Pure DOM on the `--ax-*`
 * tokens (THE TOKEN LAW), its own full-screen pointer-transparent root beside `#space-hud` (space_hud.ts untouched).
 *
 *   · the LEAD NOTE beside the target bracket — what the pip means when it is not a clean solution: "≈ medium lead —
 *     the target is manoeuvring", "≋ low lead — your sensors are jammed", "⊘ out of reach · 2.1 s" (with a dashed ghost
 *     ring where the bolt WOULD meet it), "✕ no intercept" — a glyph and words, never colour alone (WCAG 1.4.1);
 *   · the JAMMER CONTACT while I am jammed: a ◇ bracket on the pilot jamming me with its own lead pip (the target I
 *     can always fight back at — EVE's 2018 "you can always lock your jammer"), and a ◇ bearing arrow on a wider ring
 *     than the target arrow when it is off screen;
 *   · the EW STRIP under the reticle (jammed / jamming / hardened / why it ended) — its OWN line, so a live fleet war
 *     (which owns the shared prompt line half of every hour, slice F's law 7) never hides a jam;
 *   · the FIRE-GROUP CHIP: the active group and what each trigger fires, with the current keys.
 */
export interface ScreenPt { x: number; y: number }
export interface LeadHud { kind: 'lead' | 'beyond' | 'none'; confidence: 'high' | 'medium' | 'low'; label: string; pip: ScreenPt | null; bracket: ScreenPt | null }
export interface JammerHud { bracket: ScreenPt | null; lead: ScreenPt | null; arrowAng: number | null; label: string }

const ABS = 'position:absolute;transform:translate(-50%,-50%);pointer-events:none';
/** A short screen (720p at 200 % UI scale is 360 CSS px tall): the HUD around the reticle is already shoulder to
 *  shoulder, so the strip shrinks to ONE line as a top banner and the group chip yields (the swap's toast and the
 *  Loadout still say which group is live). */
export const COMPACT_MAX_H = 520;
const compact = (): boolean => innerHeight < COMPACT_MAX_H;

export class CombatHud {
  private root: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private ghost: HTMLDivElement | null = null;
  private lowRing: HTMLDivElement | null = null;
  private jamBox: HTMLDivElement | null = null;
  private jamLabel: HTMLDivElement | null = null;
  private jamLead: HTMLDivElement | null = null;
  private jamArrow: HTMLDivElement | null = null;
  private strip: HTMLDivElement | null = null;
  private chip: HTMLDivElement | null = null;
  /** Probe telemetry: what the layer shows right now. */
  readonly shown = { note: '', strip: [] as string[], tone: 'none', chip: '', jammer: '', ghost: false };

  mount(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'space-combat-hud';
    root.style.cssText = 'position:fixed;inset:0;z-index:34;pointer-events:none;font:12px var(--ax-font)';
    const mk = (css: string): HTMLDivElement => { const d = document.createElement('div'); d.style.cssText = css; d.style.display = 'none'; root.appendChild(d); return d; };
    this.note = mk('position:absolute;transform:translate(0,-50%);white-space:nowrap;font:600 11px var(--ax-mono);color:var(--ax-text);' +
      'background:color-mix(in srgb, var(--ax-bg) 80%, transparent);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:1px 6px');
    this.ghost = mk(`${ABS};width:14px;height:14px;border:1.5px dashed color-mix(in srgb, var(--ax-muted) 90%, transparent);border-radius:50%`);
    this.lowRing = mk(`${ABS};width:20px;height:20px;border:1.5px dashed color-mix(in srgb, var(--ax-warn) 90%, transparent);border-radius:50%`);
    this.lowRing.dataset.ring = 'pip'; // a ring AROUND the pip (it overlaps the pip by design)
    this.jamBox = mk(`${ABS};width:40px;height:40px;border:1.5px solid color-mix(in srgb, var(--ax-warn) 95%, transparent);transform:translate(-50%,-50%) rotate(45deg)`);
    this.jamLabel = mk('position:absolute;transform:translate(-50%,0);white-space:nowrap;font:600 11px var(--ax-mono);color:var(--ax-warn);' +
      'text-shadow:0 1px 3px var(--ax-bg)');
    this.jamLead = mk(`${ABS};width:9px;height:9px;border:1.5px solid color-mix(in srgb, var(--ax-warn) 95%, transparent);transform:translate(-50%,-50%) rotate(45deg)`);
    this.jamArrow = mk('position:absolute;left:50%;top:50%;font:700 15px var(--ax-mono);color:var(--ax-warn);text-shadow:0 1px 3px var(--ax-bg)');
    this.jamArrow.textContent = '◇';
    this.strip = mk('position:absolute;left:50%;top:calc(50% + 128px);transform:translateX(-50%);max-width:min(760px, 92vw);text-align:center;' +
      'font:600 11px var(--ax-mono);color:var(--ax-text);background:color-mix(in srgb, var(--ax-bg) 84%, transparent);' +
      'border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:4px 10px;line-height:1.45');
    this.chip = mk('position:absolute;left:50%;top:calc(50% + 106px);transform:translateX(-50%);white-space:nowrap;' +
      'font:11px var(--ax-mono);color:var(--ax-soft);opacity:.85;text-shadow:0 1px 3px var(--ax-bg)');
    document.body.appendChild(root);
    this.root = root;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
    this.note = this.ghost = this.lowRing = this.jamBox = this.jamLabel = this.jamLead = this.jamArrow = this.strip = this.chip = null;
  }

  private place(el: HTMLDivElement | null, p: ScreenPt | null): void {
    if (!el) return;
    el.style.display = p ? 'block' : 'none';
    if (p) { el.style.left = `${p.x}px`; el.style.top = `${p.y}px`; }
  }

  /** Per frame: the lead's meaning beside the bracket (null hides everything of the lead's). */
  setLead(v: LeadHud | null): void {
    this.place(this.ghost, v && v.kind === 'beyond' ? v.pip : null);
    this.place(this.lowRing, v && v.kind === 'lead' && v.confidence !== 'high' ? v.pip : null);
    const txt = v?.label ?? '';
    if (this.note) {
      const at = v && txt && v.bracket ? { x: v.bracket.x - 17, y: v.bracket.y + 30 } : null; // under the bracket, left-aligned
      this.place(this.note, at);
      if (at) {
        if (this.note.textContent !== txt) this.note.textContent = txt;
        // Never off the screen's edge: a note that would run past the right edge sits LEFT of the bracket instead.
        const wN = this.note.offsetWidth;
        if (at.x + wN > innerWidth - 6) this.note.style.left = `${Math.max(4, innerWidth - 6 - wN)}px`;
      }
    }
    this.shown.note = v && v.bracket ? txt : '';
    this.shown.ghost = !!(v && v.kind === 'beyond' && v.pip);
  }

  /** Per frame: the pilot jamming ME (null when nobody is). */
  setJammer(v: JammerHud | null): void {
    this.place(this.jamBox, v?.bracket ?? null);
    this.place(this.jamLead, v?.lead ?? null);
    if (this.jamLabel) {
      const at = v?.bracket ? { x: v.bracket.x, y: v.bracket.y + 30 } : null;
      this.jamLabel.style.display = at ? 'block' : 'none';
      if (at) {
        if (this.jamLabel.textContent !== v!.label) this.jamLabel.textContent = v!.label;
        const half = this.jamLabel.offsetWidth / 2; // centred under the bracket, but never past a screen edge
        this.jamLabel.style.left = `${Math.min(innerWidth - half - 4, Math.max(half + 4, at.x))}px`;
        this.jamLabel.style.top = `${at.y}px`;
      }
    }
    if (this.jamArrow) {
      const a = v && !v.bracket ? v.arrowAng : null;
      this.jamArrow.style.display = a !== null && a !== undefined ? 'block' : 'none';
      if (a !== null && a !== undefined) {
        const r = 96; // wider than the target arrow's 72 px ring — the two never sit on each other
        this.jamArrow.style.transform = `translate(calc(-50% + ${Math.sin(a) * r}px), calc(-50% + ${-Math.cos(a) * r}px))`;
      }
    }
    this.shown.jammer = v ? v.label : '';
  }

  /** ~5 Hz: the EW strip. */
  setStrip(tone: 'jammed' | 'jamming' | 'hardened' | 'ended' | 'none', full: readonly string[], short = ''): void {
    if (!this.strip) return;
    const small = compact() && !!short;
    const lines = small ? [short] : full;
    // Full: under the reticle and the chip. Compact: ONE line as a banner at the top (below the wallet chip and the target
    // panel, left of the toast column) — the band around the reticle is where the jammer's contact must stay visible.
    this.strip.style.top = small ? '64px' : 'calc(50% + 128px)';
    this.strip.style.transform = 'translateX(-50%)';
    this.strip.style.whiteSpace = small ? 'nowrap' : 'normal';
    const on = tone !== 'none' && lines.length > 0;
    this.strip.style.display = on ? 'block' : 'none';
    if (on) {
      this.strip.style.borderColor = tone === 'jammed' ? 'var(--ax-warn)' : tone === 'jamming' ? 'var(--ax-info)' : 'var(--ax-line2)';
      const key = lines.join('\n');
      if (this.strip.dataset.text !== key) { // names are data, never markup: text nodes only
        this.strip.dataset.text = key;
        this.strip.replaceChildren(...lines.map((l) => { const d = document.createElement('div'); d.textContent = l; return d; }));
      }
    }
    this.shown.strip = on ? [...lines] : [];
    this.shown.tone = on ? tone : 'none';
  }

  /** ~5 Hz: the fire-group chip ('' hides it). */
  setChip(text: string): void {
    if (!this.chip) return;
    if (compact()) text = '';
    this.chip.style.display = text ? 'block' : 'none';
    if (this.chip.textContent !== text) this.chip.textContent = text;
    this.shown.chip = text;
  }
}
